import { useRef } from 'react'
import { Question } from "../types"

// Master toggle for the `useRenderLog` diagnostic hook below. Wired to
// Vite's build-time `DEV` flag - true under `vite dev`, false in
// `vite build` output - so production bundles silently skip the
// console.log. Override locally by editing this line if you want
// renders logged in a production build for one-off debugging.
export const DEBUG_MODE = import.meta.env.DEV

// Diagnostic hook - logs every render of the calling component with a
// short reason: which props changed, or "no prop change" meaning the
// render was triggered internally (local state or a hook subscription).
// Always calls the same hooks so it's safe to leave in production code
// behind the `DEBUG_RENDERS` flag.
export function useRenderLog(label: string, props: Record<string, any> = {}): void {
    const countRef = useRef(0)
    const prevRef = useRef<Record<string, any>>(props)
    if (!DEBUG_MODE) return
    countRef.current += 1
    const changed: string[] = []
    for (const k of Object.keys(props)) {
        if (prevRef.current[k] !== props[k]) changed.push(k)
    }
    prevRef.current = { ...props }
    console.log(
        `${label} render #${countRef.current}`,
        changed.length > 0 ? `props changed: ${changed.join(',')}` : 'no prop change (local state or context)',
    )
}

// Parses a server timestamp. If the string carries no Z/offset it's
// treated as UTC (the server returns naive UTC stamps); otherwise
// parsed as-is. Returns NaN for unparseable input.
export function parseUtcTimestamp(s: string): number {
    if (!s) return NaN
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s)
    return Date.parse(hasTz ? s : s.replace(' ', 'T') + 'Z')
}

// Compact "time ago" formatter - keeps the width short regardless of
// elapsed time. Accepts a ms epoch or an ISO string. Returns '-' for
// unparseable input.
export function formatDurationSince(input: number | string): string {
    const t = typeof input === 'string' ? parseUtcTimestamp(input) : input
    if (!Number.isFinite(t)) return '-'
    const sec = Math.max(0, Math.round((Date.now() - t) / 1000))
    if (sec < 60) return `${sec}s`
    const min = Math.floor(sec / 60)
    if (min < 60) return `${min}m`
    const hr = Math.floor(min / 60)
    if (hr < 24) return `${hr}h`
    const day = Math.floor(hr / 24)
    if (day < 30) return `${day}d`
    const mo = Math.floor(day / 30)
    if (mo < 12) return `${mo}mo`
    return `${Math.floor(day / 365)}y`
}

export type ToastType = 'success' | 'error' | 'info' | 'warn'

export type ToastFn = (type: ToastType, message: string, duration?: number) => void
// updateQuestion accepts either a static patch or a function that receives
// the current question and returns the patch. The function form is needed
// when the new value depends on the current one (e.g. taskstream answer
// chunks appending to the existing answer text).
export type UpdateQuestionFn = (
    sha: string,
    patch: Partial<Question>,
) => void


export function buildCoreUrl(path: string): string {
    const normalizedPath = path.replace(/^\/+/, '')
    const { hostname, origin } = window.location
    const normalizedHost = hostname.toLowerCase()

    if (normalizedHost === 'localhost' || normalizedHost === '127.0.0.1' || normalizedHost === '::1' || normalizedHost === '[::1]') {
        return `${origin}/${normalizedPath}`
    }

    if (normalizedHost === 'www.contractfinderpro.com') {
        return `https://core.contractfinderpro.com/${normalizedPath}`
    }

    const envHostMatch = normalizedHost.match(/^(?:www\.)?(.+)\.contractfinderpro\.com$/)
    if (envHostMatch) {
        return `https://core.${envHostMatch[1]}.contractfinderpro.com/${normalizedPath}`
    }

    return `https://core.contractfinderpro.com/${normalizedPath}`
}

export function buildCoreApiUrl(path: string): string {
    const normalizedPath = path.replace(/^\/+/, '')
    console.log(path);
    const hostname = window.location.hostname.toLowerCase()
    if (hostname.includes('localhost')) return `http://localhost:9999/${normalizedPath}`

    const envHostMatch = hostname.match(/^(?:www\.)?(beta[a-z0-9-]*)\.contractfinderpro\.com$/)
    if (envHostMatch) return `https://api-${envHostMatch[1]}.contractfinderpro.com/${normalizedPath}`

    return `https://api.contractfinderpro.com/${normalizedPath}`
}

// "Has the user started typing an answer?" - true when the structured
// answer has any text in its first paragraph's first line's first
// sentence. That's the slot TaResponse writes on the very first
// keystroke, so it's the canonical probe now that the flat-text
// `.answer` mirror is gone from AnswerWebResult.
export function hasAnswerText(question: Question | null | undefined): boolean {
    const s = (question?.answerobj as any)?.answerstructure?.paragraphs?.[0]?.lines?.[0]?.sentences?.[0]?.sentence
    return !!s && s.trim().length > 0
}

export async function getIp(): Promise<string> {
    try {
        const r = await fetch('https://api.ipify.org?format=json')
        return (await r.json()).ip
    } catch { return 'null' }
}

// Canonical values + display labels for `bideval.scoretypestring`.
// Shared by every surface that lets the user pick a scoring style
// (ScoreFixTab toolbar, BidQuestionScoringTab toolbar, …).
export const SCORE_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: 'infoonly', label: 'Info Only' },
    { value: 'passfail', label: 'Pass / Fail' },
    { value: 'scored', label: 'Scored' },

]

// Translate a `scoretypestring` value into its display label. Falls
// back to the value itself for unrecognised strings (so a stale enum
// from the server still surfaces something readable) and returns '' for
// null / undefined.
export function scoreTypeLabel(value: string | null | undefined): string {
    if (!value) return ''
    return SCORE_TYPE_OPTIONS.find(o => o.value === value)?.label ?? value
}
